"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.initialize = exports.getInstance = exports.Pulse = void 0;
const pulse_sdk_1 = __importDefault(require("@spt-tracking/pulse-sdk"));
const mono_scc_1 = require("@snoam/mono-scc");
const enums_1 = require("./enums");
const pulseEventModel_1 = __importDefault(require("./pulseEventModel"));
const navigation_1 = require("./utils/navigation");
const getCookie_1 = require("./utils/getCookie");
const getPulseProviderId_1 = require("./utils/getPulseProviderId");
const isTestOrLocal_1 = require("./utils/isTestOrLocal");
const lodash_1 = require("lodash");
const urlHistory = [];
if (typeof document !== "undefined") {
    urlHistory.push(document.location.href);
    urlHistory.push(document.referrer);
}
let pulse;
class Pulse {
    tracker;
    user;
    subscriptionName;
    merchant;
    urlParamsToRemove;
    removeIdsInPath;
    constructor(merchant, options) {
        this.merchant = merchant;
        this.urlParamsToRemove = options?.urlParamsToRemove ?? null;
        this.removeIdsInPath = options?.removeIdsInPath ?? false;
        this.user = options?.userId;
        this.tracker = new pulse_sdk_1.default((0, getPulseProviderId_1.getPulseProviderId)(merchant), options?.config, {
            actor: this.user ? this.actor : undefined,
        });
    }
    isSeRealm() {
        const seBrands = [
            mono_scc_1.ClientShortHandName.AB,
            mono_scc_1.ClientShortHandName.WELLOBE,
            mono_scc_1.ClientShortHandName.OMNI,
            mono_scc_1.ClientShortHandName.SVD,
            mono_scc_1.ClientShortHandName.SUPERPAKETET
        ];
        return seBrands.includes(this.merchant);
    }
    getOriginUrl(eventType) {
        let originUrl;
        if (eventType === enums_1.EventType.VIEW) {
            if (urlHistory[0] != document.location.href) {
                urlHistory.pop();
                urlHistory.splice(0, 0, document.location.href);
            }
            if (urlHistory.length > 1)
                originUrl = urlHistory[1];
        }
        else if (eventType === enums_1.EventType.ENGAGEMENT) {
            originUrl = document.location.href;
        }
        return originUrl ? this.sanitizeUrl(originUrl) : "unknown";
    }
    track(model, pulseEventType) {
        const eventType = pulseEventType || (model.type === enums_1.EventType.ENGAGEMENT ? enums_1.PulseEventType.ENGAGEMENT_EVENT : enums_1.PulseEventType.TRACKER_EVENT);
        if ((0, isTestOrLocal_1.isTest)()) {
            model.deployStage = "dev";
            model.deployTag = "PulseUnicorn";
        }
        return this.tracker.track(eventType, model.toObject());
    }
    get actor() {
        return {
            id: this.user,
            realm: this.isSeRealm() ? "schibsted.com" : "spid.no",
            subscriptionName: this.subscriptionName,
        };
    }
    get provider() {
        return {
            id: (0, getPulseProviderId_1.getPulseProviderId)(this.merchant),
            type: "Organization",
            component: this.isSeRealm() ? "schibsted-se" : "schibsted-abo",
        };
    }
    get merchantName() {
        return this.merchant;
    }
    sanitizeUrl(urlString) {
        if (urlString) {
            const url = new URL(urlString);
            if (this.removeIdsInPath) {
                url.pathname = url.pathname.replace(/\/\d+(?!\w)/g, "/hidden_ids");
            }
            if (this.urlParamsToRemove && this.urlParamsToRemove.length > 0) {
                this.urlParamsToRemove.forEach((obj) => {
                    if (url.searchParams && url.searchParams.get(obj)) {
                        url.searchParams.set(obj, "removed");
                    }
                });
            }
            return url.href;
        }
        return urlString || "";
    }
    setUser(user, subscriptionName) {
        this.user = user;
        this.subscriptionName = subscriptionName;
        this.tracker.update({ actor: this.actor });
    }
    setConsents(consents) {
        this.tracker.setConsents(consents);
    }
    createGeneralModel(model) {
        return new pulseEventModel_1.default(model);
    }
    createInitialModel(model) {
        const { id, label, labelPrefix, origin = {}, custom = {}, elementType, pageId, pageType } = model;
        const abx2 = (0, getCookie_1.getCookie)("abx2");
        if (abx2) {
            custom["spt:abTestValue"] = abx2;
        }
        return {
            object: {
                id,
                name: labelPrefix ? `${labelPrefix} | ${label}` : label,
                url: this.sanitizeUrl(location.href),
                ...(elementType ? { elementType } : {}),
                ...(custom && !(0, lodash_1.isEmpty)(custom) ? { "spt:custom": custom } : {}),
                ...((pageId || pageType) ? { page: { ...(pageId ? { id: pageId } : {}), type: pageType || enums_1.ObjectType.PAGE } } : {}),
            },
            origin,
            provider: this.provider,
        };
    }
    createPageViewModel(pageViewModel) {
        const initialModel = this.createInitialModel(pageViewModel);
        initialModel["type"] = enums_1.EventType.VIEW;
        initialModel.object["type"] = pageViewModel.type || enums_1.ObjectType.PAGE;
        initialModel.object["navigationType"] = (0, navigation_1.getNavigationType)();
        initialModel.origin = {
            url: initialModel.origin.url || this.getOriginUrl(enums_1.EventType.VIEW),
            ...initialModel.origin,
        };
        if (pageViewModel.salesPosterType)
            initialModel.object["salesPosterType"] = pageViewModel.salesPosterType;
        return new pulseEventModel_1.default(initialModel);
    }
    createEngagementModel(engagementModel) {
        const initialModel = this.createInitialModel(engagementModel);
        initialModel["type"] = enums_1.EventType.ENGAGEMENT;
        initialModel["action"] = engagementModel.action;
        initialModel.object["type"] = engagementModel.type || enums_1.ObjectType.UIELEMENT;
        initialModel.origin = {
            id: initialModel.origin.id || engagementModel.pageId,
            type: initialModel.origin.type || engagementModel.pageType || enums_1.ObjectType.PAGE,
            url: initialModel.origin.url || this.getOriginUrl(enums_1.EventType.ENGAGEMENT),
            ...initialModel.origin,
        };
        if (engagementModel.intent)
            initialModel["intent"] = engagementModel.intent;
        if (engagementModel.target)
            initialModel["target"] = engagementModel.target;
        return new pulseEventModel_1.default(initialModel);
    }
}
exports.Pulse = Pulse;
function getInstance() {
    return pulse;
}
exports.getInstance = getInstance;
function initialize(merchant, options) {
    pulse = new Pulse(merchant, options);
    return pulse;
}
exports.initialize = initialize;
